import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { FormEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { EMAIL_REGEX_STRING, MIN_LENGTH_PASSWORD } from '../../constants';
import socketHandler from '../../helpers/socket';
import { getSignInUrl, login } from '../../store/actions/auth';
import Button from '../FormComponents/Button';
import TextField from '../FormComponents/TextField';
import ToastMessage from '../ToastMessage/ToastMessage';

function Login() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [emailValid, setEmailValid] = useState(true);
  const [withPassword, setWithPassword] = useState(!!location?.state?.withPassword);
  const [signInCodeSent, setSignInCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (location?.state?.message) {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    }
  }, []);

  const loginFunc = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    setLoading(true);
    const fromLocation = location?.state?.from || window?.history?.state.from;
    if (!withPassword) {
      dispatch(
        getSignInUrl({ email, fromLocation: fromLocation !== '/' ? fromLocation : '' }, (res, message) => {
          setLoading(false);
          if (res === true) {
            setSignInCodeSent(true);
          } else {
            setToastType('error');
            setToast(message || 'Sending sign in code failed');
          }
        }),
      );
    } else {
      dispatch(
        login({ email, password }, (res, message) => {
          setLoading(false);
          if (res === true) {
            socketHandler();
            if (fromLocation) {
              navigate(fromLocation);
            } else {
              navigate('/', { replace: true });
            }
          } else {
            setToastType('error');
            setToast(message || 'Login failed');
          }
        }),
      );
    }
  };

  const handleEmailChange = e => {
    setEmailValid(!!e.target.validity.valid);
    setEmail(e.target.value);
  };

  if (signInCodeSent) {
    return (
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Box display='flex' mt={5}>
          <Typography>Please check your email for the sign in link. </Typography>
          <CheckCircleIcon fontSize='medium' htmlColor='green' />
        </Box>
        <Typography variant='caption'>Note that it will expire after 10 minutes.</Typography>
        <Typography
          display='flex'
          mt={1}
          style={{
            textDecoration: 'underline',
            color: '#0e73e0',
            cursor: 'pointer',
          }}
          onClick={() => {
            setSignInCodeSent(false);
            setWithPassword(true);
          }}
        >
          Sign in with password
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <Box
        component='form'
        sx={{
          '& > :not(style)': { mt: 1.5, mb: 1.5, width: '100%' },
        }}
        onSubmit={loginFunc}
      >
        <TextField
          label='Email'
          value={email}
          onChange={handleEmailChange}
          error={!emailValid}
          inputProps={{ pattern: EMAIL_REGEX_STRING }}
          helperText={!emailValid ? 'Please input a valid email' : ''}
          required
        />
        {withPassword && (
          <TextField
            label='Password'
            type='password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            inputProps={{ minLength: MIN_LENGTH_PASSWORD }}
            required
          />
        )}
        <Button loading={loading} text={withPassword ? 'Login' : 'Continue with email'} style={{ marginTop: '20px' }} />
        <Box display='flex' justifyContent='space-between'>
          <Typography
            display='flex'
            justifyContent='flex-end'
            style={{
              textDecoration: 'underline',
              color: '#0e73e0',
              cursor: 'pointer',
            }}
            onClick={() => setWithPassword(prev => !prev)}
          >
            {`Sign in with ${withPassword ? 'email url' : 'password'}`}
          </Typography>
          <Typography display='flex' justifyContent='flex-end'>
            <Link to='/reset-password'>Forgot password?</Link>
          </Typography>
        </Box>
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType || 'success'} />
    </div>
  );
}

export default Login;
